import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import TestimonialSlider from '../components/TestimonialSlider';
import { Building, CheckCircle } from 'lucide-react';

const CompanyFormation = () => {
  const steps = [
    {
      title: 'Information Collection',
      description: 'We gather essential business details including company name, business structure, ownership information, registered agent details, and state filing requirements to ensure proper formation.',
    },
    {
      title: 'Document Preparation',
      description: 'Our legal team prepares all necessary formation documents including Articles of Incorporation/Organization, Operating Agreements, and by-laws customized to your business needs.',
    },
    {
      title: 'Filing and Registration',
      description: 'We file your formation documents with the Secretary of State, obtain your EIN from the IRS, and provide you with all necessary certificates and documentation to operate legally.',
    },
  ];

  const packages = [
    {
      name: 'START YOUR BUSINESS WITH EASE',
      price: 'Custom Pricing',
      priceNote: '',
      features: [
        'Make sure you are not liable for any business debts. Ensure you are not on the hook for business liabilities',
        'Company Formation through a Simple Process: Our 3 step process discards with entering your details, Then selecting a service and lastly payment.',
        'Save Money on Taxes: For potential savings you get to decide whether you are taxed as a LLC or a Corporation',
      ],
      popular: true,
    },
  ];

  const usps = [
    {
      title: 'Business Law Expertise',
      description: 'Our team includes experienced business attorneys who understand the legal complexities of company formation across all 50 states. We ensure your business is properly structured from day one.',
    },
    {
      title: 'Fast Formation',
      description: 'We expedite the formation process and can typically complete state filings within 3-5 business days. Rush processing is available for urgent business needs.',
    },
    {
      title: 'Complete Service',
      description: 'From selecting the right business structure to obtaining your EIN and setting up compliance, we handle every aspect of company formation so you can focus on building your business.',
    },
    {
      title: 'Ongoing Compliance',
      description: 'Beyond formation, we provide ongoing support with annual reports, registered agent services, and compliance reminders to keep your business in good standing.',
    },
  ];

  const testimonials = [
    {
      name: 'Robert Williams',
      company: 'Tech Innovations LLC',
      text: 'Starting my LLC was incredibly easy with Fine Trademark. They handled all the paperwork and had everything filed within days. Highly professional service.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop',
    },
    {
      name: 'Sandra Martinez',
      company: 'Boutique Retail Inc.',
      text: 'The corporation formation package was comprehensive and affordable. Their team answered all my questions and made the process stress-free.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop',
    },
    {
      name: 'Kevin Brown',
      company: 'Consulting Services LLC',
      text: 'Excellent service from consultation to final documents. They helped me choose the right business structure and completed everything efficiently.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop',
    },
    {
      name: 'Lisa Chen',
      company: 'Creative Agency Inc.',
      text: 'The premium package was worth every penny. They handled everything including banking setup and S-Corp election. True professionals.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop',
    },
    {
      name: 'Thomas Anderson',
      company: 'Property Management LLC',
      text: 'Fast, affordable, and thorough. Fine Trademark made company formation simple and got my business up and running quickly.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop',
    },
  ];

  const faqs = [
    {
      question: 'Should I form an LLC or Corporation?',
      answer: 'The choice depends on your business goals. LLCs offer flexibility and simpler management, ideal for small businesses and startups. Corporations provide better options for raising capital and have more formal structure, suitable for larger businesses planning significant growth or investment.',
    },
    {
      question: 'How long does company formation take?',
      answer: 'Standard processing typically takes 5-10 business days depending on the state. We file your documents within 24-48 hours of receiving all information. Expedited processing options are available for faster formation.',
    },
    {
      question: 'What is a registered agent and do I need one?',
      answer: 'A registered agent is required in all states to receive legal documents and state correspondence on behalf of your company. We provide registered agent service included in all our packages.',
    },
    {
      question: 'Do I need an EIN for my new company?',
      answer: 'Yes, an EIN (Employer Identification Number) is required for most businesses to open bank accounts, hire employees, and file taxes. We obtain your EIN from the IRS as part of our formation service.',
    },
    {
      question: 'What ongoing requirements does my company have?',
      answer: 'Most states require annual reports and franchise tax payments. We provide compliance calendars and reminders to help you stay in good standing. Our registered agent service ensures you never miss important state notices.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1E1E1E] via-[#2a2a2a] to-[#1E1E1E] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="page-title text-white mb-6 animate-in fade-in slide-in-from-top duration-700">
              Company Formation Services
            </h1>
            <p className="body-large text-gray-300 mb-8 animate-in fade-in slide-in-from-bottom duration-700">
              Start your business right with professional company formation. Form your LLC or Corporation quickly and efficiently with expert legal support.
            </p>
            <Link to="/contact-us">
              <Button size="lg" className="bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105">
                Form Your Company Today
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Service */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="section-heading">About Company Formation</h2>
              <p className="body-regular">
                Company formation is the legal process of establishing your business as a recognized entity. Whether you choose to form an LLC (Limited Liability Company) or Corporation, proper formation provides liability protection, tax benefits, and credibility for your business.
              </p>
              <p className="body-regular">
                Our comprehensive company formation service handles all aspects of establishing your business, from selecting the appropriate structure and filing state documents to obtaining your EIN and setting up compliance requirements. We make starting your business simple and stress-free.
              </p>
            </div>
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800"
                alt="Company Formation"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Our Formation Process</h2>
            <p className="body-large max-w-3xl mx-auto">
              We streamline company formation with a proven three-step process that gets your business up and running quickly.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-[#E5B80B] text-[#1E1E1E] flex items-center justify-center text-3xl font-bold mb-6 shadow-lg">
                    {index + 1}
                  </div>
                  <h3 className="sub-heading mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-[#E5B80B]/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Company Formation Packages</h2>
            <p className="body-large max-w-3xl mx-auto">
              Choose the formation package that matches your business needs. All packages include professional service and state filing.
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            {packages.map((pkg, index) => (
              <Card key={index} className={`relative ${pkg.popular ? 'border-4 border-[#E5B80B] shadow-2xl' : 'border-2'}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-[#E5B80B] text-[#1E1E1E] text-sm px-4 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="pricing-card p-6">
                  <div className="pricing-header">
                    <h3 className="card-title text-white mb-2">{pkg.name}</h3>
                    <div className="border-t border-white/30 w-12 mx-auto my-2"></div>
                    <span className="text-4xl font-bold">{pkg.price}</span>
                  </div>
                  <div className="pricing-features mt-6">
                    <ul className="space-y-3">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-[#E5B80B] mr-2 mt-0.5 flex-shrink-0" />
                          <span className="body-small text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-gray-500 mb-4 text-center mt-4">{pkg.priceNote}</p>
                  <Link to="/contact-us">
                    <Button className={`w-full h-12 font-semibold transition-all duration-300 mt-auto ${
                      pkg.popular
                        ? 'bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E]'
                        : 'bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white'
                    }`}>
                      Get Started
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Why Choose Our Formation Services</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              <p className="body-regular">
                Starting a business involves numerous legal and administrative steps. Our experienced team simplifies this process by handling all aspects of company formation, ensuring your business is properly established and compliant from day one.
              </p>
              <p className="body-regular">
                We've helped thousands of entrepreneurs form their companies across all industries. Our comprehensive service includes expert consultation to help you choose the right business structure, complete document preparation, and ongoing support to maintain compliance.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {usps.map((usp, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <h3 className="sub-heading mb-4">{usp.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{usp.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#E5B80B] to-[#c99f09]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="section-heading mb-6">Ready to Start Your Business?</h2>
          <p className="text-xl text-[#1E1E1E]/90 mb-8 leading-relaxed">
            Form your company today with professional legal support. Get your business up and running quickly and correctly.
          </p>
          <Link to="/contact-us">
            <Button size="lg" className="bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105">
              Start Your Formation
            </Button>
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="section-heading mb-4">Client Success Stories</h2>
          </div>
          <TestimonialSlider testimonials={testimonials} />
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-semibold text-[#1E1E1E] hover:text-[#E5B80B]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default CompanyFormation;
